import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Box, Button, Card, Grid, Stack, Typography, DateTimeStandard } from "@curves/ui-base";
import { ReadTextField, TabsCardLayout } from "@curves/ui-shared";
import { parseDate } from "@util/moment";
import { isEqual } from "@util/utils";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import useClubStockOutApi from "@apis/useClubStockOutApi";
import useTabState from "@util/hook/useTabState";
import ShipmentProductList from "./components/ShipmentProductList";
import ShipmentRecordList from "./components/ShipmentRecordList";

// 功能：出貨單明細頁籤代碼
const TAB_VALUES = {
	PRODUCT_LIST: "product-list",
	SHIPMENT_RECORD: "shipment-record",
};

// 功能：出貨單明細頁籤定義
const TABS = [
	{ label: "商品清單", value: TAB_VALUES.PRODUCT_LIST },
	{ label: "出貨紀錄", value: TAB_VALUES.SHIPMENT_RECORD },
];

// 功能：Tabs 內容區樣式
const TABS_CONTENT_PROPS = { className: "px-3 pb-3" };

// 功能：出貨單明細（狀態、摘要、商品清單/入庫紀錄、結單/驗收操作）
const ShipmentDetailPage = () => {
	const { shipmentID } = useParams();
	const { getClubStockOutApi, getClubStockOutRecordApi } = useClubStockOutApi();
	const [shipment, setShipment] = useState(null);
	const [shipmentRecords, setShipmentRecords] = useState([]);
	const { activeTab: tabValue, handleTabChange } = useTabState(TAB_VALUES.PRODUCT_LIST);

	const fetchShipment = useCallback(
		async id => {
			try {
				const resp = await getClubStockOutApi(id);
				if (resp) setShipment(resp);
			} catch (e) {
				setShipment(null);
			}
		},
		// eslint-disable-next-line
		[getClubStockOutApi],
	);

	const fetchShipmentRecords = useCallback(
		async id => {
			try {
				const resp = await getClubStockOutRecordApi(id);
				setShipmentRecords(Array.isArray(resp) ? resp : []);
			} catch (e) {
				setShipmentRecords([]);
			}
		},
		// eslint-disable-next-line
		[getClubStockOutRecordApi],
	);

	// 功能：載入出貨單摘要（頁面進入即需顯示）
	useEffect(() => {
		if (!shipmentID) {
			setShipment(null);
			return;
		}
		fetchShipment(shipmentID);
	}, [shipmentID, fetchShipment]);

	// 功能：切換至出貨紀錄 Tab 時載入紀錄
	useEffect(() => {
		if (!shipmentID || !isEqual(tabValue, TAB_VALUES.SHIPMENT_RECORD)) return;
		fetchShipmentRecords(shipmentID);
	}, [tabValue, shipmentID, fetchShipmentRecords]);

	// 功能：轉換出貨明細商品為表格資料
	const productList = (shipment?.items ?? []).map(item => ({
		productID: item.productID,
		sku: item.sku,
		productCode: item.sku,
		productName: item.productName,
		name: item.productName,
		spec: item.variantName ?? "",
		quantity: item.quantity,
	}));

	return (
		<Box className="px-3 py-2 shipment-detail-page">
			{/* 標題列：出貨單明細 / 返回總覽 */}
			<Stack justifyContent="space-between" alignItems="center" className="mb-2">
				<Typography variant="h5" className="mb-0 font-weight-bold">
					出貨單明細
				</Typography>
				<Button component={NavLink} to={CLUB_INVENTORY_ROUTER.SHIPMENT.OVERVIEW} variant="contained">
					返回總覽
				</Button>
			</Stack>

			{/* 出貨單摘要 */}
			<Card className="p-3 mb-3">
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="出貨單編號">{shipment?.stockOutNo ?? "—"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="建單日期">{shipment?.createdDate ? parseDate(shipment.createdDate, DateTimeStandard.DisplayDateBySlash) : "—"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="出貨日期">{shipment?.stockOutDate ? parseDate(shipment.stockOutDate, DateTimeStandard.DisplayDateBySlash) : "—"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="擔當">{shipment?.employeeName ?? "-"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="出貨類型">{shipment?.stockOutTypeText ?? "-"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<ReadTextField label="收貨對象">{shipment?.targetClubName ?? "-"}</ReadTextField>
					</Grid>
					<Grid item xs={12} sm={6}>
						<ReadTextField label="原因">{shipment?.reason ?? "-"}</ReadTextField>
					</Grid>
					{shipment?.notes && (
						<Grid item xs={12}>
							<ReadTextField label="備註">{shipment.notes}</ReadTextField>
						</Grid>
					)}
				</Grid>
			</Card>

			{/* Tabs：商品清單（之後可擴充其他 Tab） */}
			<TabsCardLayout className="shipment-detail-tabs" tabs={TABS} activeTab={tabValue} onTabChange={handleTabChange} actions={undefined} tabsContent={TABS_CONTENT_PROPS}>
				{tabValue === TAB_VALUES.PRODUCT_LIST && <ShipmentProductList products={productList} className="mt-3" />}

				{tabValue === TAB_VALUES.SHIPMENT_RECORD && <ShipmentRecordList records={shipmentRecords} className="mt-3" />}
			</TabsCardLayout>
		</Box>
	);
};

export default ShipmentDetailPage;
